import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, Trash2, Loader2, ChevronUp, ChevronDown, Plus, Star } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { GalleryImage } from './ImageGalleryManager';

interface SectionImageGalleryManagerProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  storagePath: string;
}

export function SectionImageGalleryManager({ 
  images, 
  onChange, 
  storagePath,
}: SectionImageGalleryManagerProps) {
  const { toast } = useToast();
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${storagePath}/section_${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('town-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('town-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
      return null;
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingIndex(-1);

    const newImages: GalleryImage[] = [];
    
    for (const file of Array.from(files)) {
      const url = await uploadImage(file);
      if (url) {
        newImages.push({ url, attribution: '' });
      }
    }

    if (newImages.length > 0) {
      onChange([...images, ...newImages]);
      toast({
        title: `${newImages.length} image(s) added`,
        description: 'Remember to save your changes.',
      });
    }

    setUploadingIndex(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReplaceImage = async (index: number, file: File) => {
    setUploadingIndex(index);

    const url = await uploadImage(file);
    if (url) {
      const updated = [...images];
      updated[index] = { ...updated[index], url };
      onChange(updated);
      toast({
        title: 'Image replaced',
        description: 'Remember to save your changes.',
      });
    }

    setUploadingIndex(null);
  };

  const updateAttribution = (index: number, attribution: string) => {
    const updated = [...images];
    updated[index] = { ...updated[index], attribution };
    onChange(updated);
  };

  const removeImage = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const moveImage = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= images.length) return;
    const updated = [...images];
    const [movedItem] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, movedItem);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Section Images</Label>
        <span className="text-xs text-muted-foreground">{images.length} image(s)</span>
      </div>

      {/* Image grid - compact for sections */}
      {images.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <div className="relative aspect-video rounded-lg overflow-hidden border">
                <img
                  src={image.url}
                  alt={`Section image ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                {/* Controls overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => moveImage(index, index - 1)}
                    disabled={index === 0}
                    title="Move left"
                  >
                    <ChevronUp className="h-3 w-3 -rotate-90" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) handleReplaceImage(index, file);
                      };
                      input.click();
                    }}
                    disabled={uploadingIndex === index}
                  >
                    {uploadingIndex === index ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => moveImage(index, index + 1)}
                    disabled={index === images.length - 1}
                    title="Move right"
                  >
                    <ChevronDown className="h-3 w-3 -rotate-90" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => removeImage(index)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                {/* Position badge */}
                <div className="absolute bottom-1 left-1">
                  {index === 0 ? (
                    <span className="bg-primary text-primary-foreground px-1.5 py-0.5 rounded text-[10px] font-medium flex items-center gap-0.5">
                      <Star className="h-2.5 w-2.5" /> Main
                    </span>
                  ) : (
                    <span className="bg-background/80 px-1.5 py-0.5 rounded text-[10px] font-medium">
                      {index + 1}
                    </span>
                  )}
                </div>
              </div>
              {/* Attribution input */}
              <Input
                value={image.attribution || ''}
                onChange={(e) => updateAttribution(index, e.target.value)}
                placeholder="Photo credit"
                className="mt-1 text-xs h-7"
              />
            </div>
          ))}
        </div>
      )}

      {/* Add images button */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadingIndex !== null}
        className="w-full h-16 border-dashed"
      >
        {uploadingIndex === -1 ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Uploading...
          </>
        ) : (
          <>
            <Plus className="h-4 w-4 mr-2" />
            Add Images
          </>
        )}
      </Button>
      <p className="text-xs text-muted-foreground">
        First image is displayed as the main section photo.
      </p>
    </div>
  );
}
